import React, { useEffect, useRef, useState } from 'react';
import { loadDeviceLogCaptures, loadSiteControlSnapshot, readDeviceLogPage, downloadDeviceLog, cancelDeviceLog, deleteDeviceLog, saveDeviceLogsToServer } from './siteControlApi';
import { deviceName } from './deviceUpdateWorkflow';
import { LOG_TYPES, LOG_LEVELS, DEFAULT_LEVELS, collectDeviceLogs, buildDeviceLogPackage, saveLogDownload } from './deviceLogPackage';

const pending = (capture) => ['queued', 'running'].includes(capture.status);
const CAPTURE_FAILURE = {
  adb_log_invalid: 'The relay rejected the snapshot request. Check that the relay and server versions match.',
  adb_log_busy: 'The relay was already collecting another snapshot. Try again when it finishes.',
  adb_identity_unavailable: 'The relay could not load the site ADB identity. Check connection before trying again.',
  adb_log_connect_failed: 'The separate ADB connection for log capture failed before a read could start.',
  adb_log_clock_read_failed: 'The relay could not read the target clock over ADB.',
  adb_log_clock_unsupported: 'The target did not return a usable clock value for the recent-log window.',
  adb_log_open_failed: 'The target closed or refused the separate logcat session after its clock was read.',
  adb_log_read_timeout: 'Reading existing logs timed out. No incomplete snapshot was saved.',
  adb_log_stream_incomplete: 'The log stream ended without a complete response. No partial logs were saved.',
  adb_log_read_failed: 'The ADB connection failed while reading recent logs from the target.',
  adb_log_target_rejected: 'The target reported an error while dumping its recent logs.',
  adb_log_format_unsupported: 'The target returned a log format this snapshot reader cannot interpret.',
  adb_log_failed: 'The relay could not read the snapshot and did not report the failing read stage.',
  adb_log_expired: 'The snapshot exceeded its time limit.',
  capture_expired: 'The snapshot exceeded its time limit.',
  adb_log_upload_timeout: 'The relay did not receive a storage acknowledgment in time.',
  adb_log_upload_rejected: 'The relay could not upload the snapshot to private storage.',
  capture_upload_rejected: 'The private upload could not be completed.',
  capture_relay_disconnected: 'The relay disconnected before the snapshot completed.',
  capture_integrity_failed: 'The uploaded snapshot failed its integrity check.',
  capture_scope_changed: 'The target ADB identity or endpoint changed. Check connection and try again.',
  capture_relay_unavailable: 'An eligible relay was no longer available when collection started.',
  capture_dispatch_failed: 'The server could not start collection through the selected relay.',
};
const failureMessage = (code) => typeof CAPTURE_FAILURE[code] === 'string' ? CAPTURE_FAILURE[code]
  : 'The snapshot failed or expired. No partial logs were saved.';
const CAPTURE_UNAVAILABLE = {
  storage_unavailable: 'Log storage is currently unavailable.',
  relay_update_required: 'Update the Suitestream app on an eligible relay to enable log capture. Rebuilding server containers does not update the relay app.',
  relay_busy: 'Eligible relays are capturing logs. Wait for a capture to finish, then try again.',
  relay_unavailable: 'No eligible relay is online. Connect a relay to capture new logs.',
  endpoint_stale: 'Verify the saved ADB endpoint in Pairing before capturing logs.',
  authorization_required: 'Authorize this ADB target in Pairing before capturing logs.',
  identity_changed: 'The site ADB identity changed. Pair this target again before capturing logs.',
  identity_unavailable: 'The site ADB identity is unavailable. Check Pairing before capturing logs.',
};
const MAX_BYTES = 2 * 1024 * 1024;
const newId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export default function DeviceLogSnapshots({ siteId, snapshot }) {
  const [captures, setCaptures] = useState(snapshot?.captures || []);
  const [targetIds, setTargetIds] = useState(null);
  const [logTypes, setLogTypes] = useState(['android']);
  const [levels, setLevels] = useState(DEFAULT_LEVELS);
  const [topology, setTopology] = useState(null);
  const [progress, setProgress] = useState(null);
  const [packageResult, setPackageResult] = useState(null);
  const [packageItems, setPackageItems] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  const [workLabel, setWorkLabel] = useState('');
  const [minutes, setMinutes] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [search, setSearch] = useState('');
  const alive = useRef(true);
  const readGeneration = useRef(0);
  const requests = useRef(new Map());
  const working = useRef(false);
  const stop = useRef(false);
  const wake = useRef(null);
  const savedTargets = (snapshot?.targets || []);
  const inventory = [...(snapshot?.devices || []), ...(topology?.bridgeNodes || []).map((node) => ({ deviceId: node.id, name: node.name })),
    ...(topology?.targets || [])];
  const ids = [...new Set([...savedTargets.map((target) => target.deviceId), ...inventory.map((item) => item.deviceId),
    ...captures.map((capture) => capture.targetDeviceId)])];
  const targets = ids.map((id) => savedTargets.find((target) => target.deviceId === id)
    || { deviceId: id, name: inventory.find((item) => item.deviceId === id)?.name || deviceName(id, snapshot), logCaptureAvailable: false });
  const selectedIds = targetIds || (targets.length ? [targets[0].deviceId] : []);
  const firstTargetId = targets[0]?.deviceId;
  const selectedTargets = targets.filter((target) => selectedIds.includes(target.deviceId));
  const unsupported = LOG_TYPES.some((type) => type.future && logTypes.includes(type.id));
  const available = snapshot?.diagnosticsAvailable === true;
  useEffect(() => {
    if (targetIds === null && snapshot && firstTargetId !== undefined) setTargetIds([firstTargetId]);
  }, [targetIds, snapshot, firstTargetId]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; readGeneration.current++; wake.current?.(); };
  }, []);
  useEffect(() => {
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const result = await loadSiteControlSnapshot(siteId);
        if (active) setTopology(result);
      } catch (_) { if (active) setTopology(null); }
      finally { if (active) timer = setTimeout(refresh, 15000); }
    };
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [siteId]);
  useEffect(() => {
    if (!available) return undefined;
    let active = true;
    let timer;
    const refresh = async () => {
      let delay = 10000;
      try {
        if (working.current) { delay = 2000; return; }
        const result = await loadDeviceLogCaptures(siteId);
        if (active && !working.current && result?.success) { setCaptures(result.captures); if (result.captures.some(pending)) delay = 2000; }
      } catch (_) { if (active) setError('Saved logs could not be refreshed. Try again shortly.'); }
      finally { if (active) timer = setTimeout(refresh, delay); }
    };
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [siteId, available]);
  const update = (capture) => setCaptures((previous) => [capture, ...previous.filter((item) => item.id !== capture.id)]);
  const run = async (action) => {
    if (working.current) return;
    working.current = true;
    setBusy(true); setError('');
    try { await action(); }
    catch (_) { if (alive.current) setError('The log request could not be completed. Check the saved status before trying again.'); }
    finally { working.current = false; if (alive.current) setBusy(false); }
  };
  const start = (downloadZip = false) => run(async () => {
    if (!selectedTargets.length || !logTypes.length || !levels.length || unsupported || !available
        || snapshot.expiresAtEpochMs <= Date.now()) return;
    stop.current = false; setStopping(false); setPackageResult(null); setPackageItems(null); setServerResult(null);
    setCollecting(true);
    let items;
    try {
      items = await collectDeviceLogs({ siteId, targets: selectedTargets, minutes, requests: requests.current, newId,
        isActive: () => alive.current, shouldStop: () => stop.current,
        wait: () => new Promise((resolve) => {
          const timer = setTimeout(() => { wake.current = null; resolve(); }, 2000);
          wake.current = () => { clearTimeout(timer); resolve(); };
        }), onCapture: update, onProgress: setProgress });
    } finally {
      if (alive.current) setCollecting(false);
    }
    if (!items || !alive.current) return;
    const plan = { siteId, minutes, logTypes: [...logTypes], levels: [...levels], items };
    setPackageItems(plan);
    await saveToServer(plan);
    if (downloadZip && alive.current) await downloadPackage(plan);
  });
  const saveToServer = async (plan) => {
    setWorkLabel('Saving logs to server…');
    const result = await saveDeviceLogsToServer(siteId, { minutes: plan.minutes, logTypes: plan.logTypes, levels: plan.levels,
      targets: plan.items.map((item) => ({ deviceId: item.deviceId, captureId: item.capture?.id || null })) });
    if (!alive.current) return;
    if (!result?.success || !result.saved) throw new Error('server_save_failed');
    setServerResult(result.saved);
    try {
      const refreshed = await loadDeviceLogCaptures(siteId);
      if (alive.current && refreshed?.success) setCaptures(refreshed.captures);
    } catch (_) { /* The acknowledged server save remains valid if history refresh fails. */ }
  };
  const downloadPackage = async (plan) => {
    setWorkLabel('Preparing ZIP…');
    const result = await buildDeviceLogPackage({ ...plan, isActive: () => alive.current });
    if (!result || !alive.current) return;
    setPackageResult(result.manifest);
    saveLogDownload(result.blob, `suitestream-logs-site-${siteId}-${Date.now()}.zip`);
  };
  const downloadSaved = () => run(async () => {
    const items = selectedTargets.map((target) => {
      const capture = captures.filter((entry) => entry.targetDeviceId === target.deviceId && entry.minutes === minutes)
        .sort((a, b) => b.createdAtEpochMs - a.createdAtEpochMs)[0];
      return { deviceId: target.deviceId, name: target.name, status: capture?.status || 'no_saved_capture', capture };
    });
    const plan = { siteId, minutes, logTypes: [...logTypes], levels: [...levels], items };
    setProgress(items); setPackageResult(null); setPackageItems(plan);
    await downloadPackage(plan);
  });
  const view = (capture) => run(async () => {
    const generation = ++readGeneration.current;
    const chunks = [];
    let offset = 0;
    while (true) {
      const page = await readDeviceLogPage(siteId, capture.id, offset);
      if (!alive.current || generation !== readGeneration.current) return;
      if (!page?.success || !Number.isInteger(page.nextOffset) || page.nextOffset < offset || page.nextOffset > MAX_BYTES
          || page.sizeBytes > MAX_BYTES || page.sizeBytes !== capture.sizeBytes || typeof page.data !== 'string' || page.data.length > 87384) throw new Error('invalid_page');
      const raw = window.atob(page.data);
      if (raw.length !== page.nextOffset - offset || (raw.length === 0 && !page.eof)) throw new Error('invalid_page');
      chunks.push(Uint8Array.from(raw, (char) => char.charCodeAt(0)));
      offset = page.nextOffset;
      if (page.eof) break;
    }
    if (offset !== capture.sizeBytes) throw new Error('incomplete_file');
    const bytes = new Uint8Array(offset);
    let position = 0;
    chunks.forEach((chunk) => { bytes.set(chunk, position); position += chunk.length; chunk.fill(0); });
    const text = new TextDecoder().decode(bytes); bytes.fill(0);
    setViewer({ id: capture.id, text }); setSearch('');
  });
  const download = (capture) => run(async () => {
    const blob = await downloadDeviceLog(siteId, capture.id);
    if (!alive.current) return;
    saveLogDownload(blob, `suitestream-log-${capture.id}.txt`);
  });
  const selected = captures.filter((capture) => selectedIds.includes(capture.targetDeviceId)).sort((a, b) => b.createdAtEpochMs - a.createdAtEpochMs);
  const toggle = (values, value) => values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
  const matches = viewer ? viewer.text.split('\n').filter((line) => !search || line.toLocaleLowerCase().includes(search.toLocaleLowerCase())) : [];
  const canCapture = available && snapshot?.expiresAtEpochMs > Date.now() && selectedTargets.length > 0
    && selectedTargets.some((target) => target.logCaptureAvailable) && logTypes.length > 0 && levels.length > 0
    && !unsupported && !selected.some(pending);
  return <section aria-label="Device log snapshots" className="device-config__logs">
    <div className="device-config__section-heading"><div><h3>Log Capture</h3><p>Select devices and logs, then capture them to the server.</p></div><span className="device-config__badge">1 / 5 / 15 minutes</span></div>
    <p>Reproduce the issue first, then capture the previous 1, 5 or 15 minutes of logs still retained on the target.
      Collection finishes automatically; Cancel discards an unfinished snapshot. This is not a live recording.</p>
    <p>Private snapshots expire automatically after the time shown. Saved logs can be viewed when relays are offline.</p>
    <p>Devices are collected one at a time. Each window ends at that device’s collection time.
      Up to 2 MiB / 20,000 lines per device; Android may retain less history.</p>
    <p>Server saves replace the previous temporary log set for this site, including devices no longer selected.
      They expire within 24 hours. ZIP downloads are optional.</p>
    {!available && <p>Log storage is currently unavailable.</p>}
    <fieldset className="device-config__log-options" disabled={busy}><legend>Devices</legend>
      <button type="button" onClick={() => setTargetIds(targets.map((target) => target.deviceId))}>Select all devices</button>
      <button type="button" onClick={() => setTargetIds([])}>Clear devices</button>
      {!topology && <p>Relay assignments could not be loaded yet.</p>}
      <ul className="device-config__selection">{targets.map((target) => {
        const node = topology?.bridgeNodes?.find((entry) => entry.id === target.deviceId);
        const live = snapshot?.devices?.find((entry) => entry.deviceId === target.deviceId);
        const relay = node?.bridgeCapable === true;
        const primary = topology?.site?.primaryBridgeDeviceId === target.deviceId;
        const secondary = topology?.site?.secondaryBridgeDeviceId === target.deviceId;
        return <li key={target.deviceId} className={selectedIds.includes(target.deviceId) ? 'is-selected' : ''}>
          <label><input type="checkbox" checked={selectedIds.includes(target.deviceId)}
            onChange={() => { setTargetIds(toggle(selectedIds, target.deviceId)); setViewer(null); readGeneration.current++; }} />
            <span><strong>{target.name || deviceName(target.deviceId, snapshot)}</strong><small>Device {target.deviceId}</small></span></label>
          {(relay || primary || secondary) && <span className={`device-config__badge${live?.connected ? ' device-config__badge--ready' : ''}`}>
            {primary ? 'Primary relay' : secondary ? 'Secondary relay' : 'Relay'} · {live ? live.connected ? 'Online' : 'Offline' : 'Status unknown'}
          </span>}
          {live?.adbSourceReady && <small>ADB identity ready</small>}
          {!target.logCaptureAvailable && <p>{CAPTURE_UNAVAILABLE[target.logCaptureReason]
            || (!savedTargets.some((entry) => entry.deviceId === target.deviceId)
              ? 'Configure and authorize an ADB target in Pairing to collect logs from this device.'
              : 'A compatible live relay and current ADB authorization are required for a new capture.')}</p>}
        </li>;
      })}</ul>
    </fieldset>
    <fieldset className="device-config__log-options" disabled={busy}><legend>Log types · applies to every selected device</legend>
      <button type="button" onClick={() => { setLogTypes(LOG_TYPES.map((type) => type.id)); setLevels(DEFAULT_LEVELS); }}>Select All</button>
      <button type="button" onClick={() => { setLogTypes(['android']); setLevels(DEFAULT_LEVELS); }}>Use defaults</button>
      <button type="button" onClick={() => setLogTypes([])}>Clear log types</button>
      {LOG_TYPES.map((type) => <label key={type.id} className="device-config__android-setting">
        <input type="checkbox" checked={logTypes.includes(type.id)} onChange={() => setLogTypes((previous) => toggle(previous, type.id))} />
        <span><strong>{type.label}</strong>{type.future && <span className="device-config__badge">Next capture update</span>}<small>{type.detail}</small></span>
      </label>)}
      {unsupported && <p role="status" className="device-config__notice device-config__notice--warning">
        The extra log types need the next server and Android capture update. Use defaults to capture and download the currently supported logs.</p>}
    </fieldset>
    <fieldset className="device-config__log-options" disabled={busy}><legend>Include message levels in saved logs</legend>
      <p>All levels are included by default. Choose fewer to reduce the saved files; source captures keep the full Android stream.</p>
      <button type="button" onClick={() => setLevels(DEFAULT_LEVELS)}>Select all levels</button>
      <button type="button" onClick={() => setLevels([])}>Clear levels</button>
      <div className="device-config__log-levels">{LOG_LEVELS.map(([id, label]) => <label key={id}>
        <input type="checkbox" checked={levels.includes(id)} onChange={() => setLevels((previous) => toggle(previous, id))} />{label}
      </label>)}</div>
    </fieldset>
    <div className="device-config__toolbar">
    <label>Recent window <select disabled={busy} value={minutes} onChange={(event) => setMinutes(Number(event.target.value))}>
      {[1, 5, 15].map((value) => <option key={value} value={value}>{value} minute{value > 1 ? 's' : ''}</option>)}
    </select></label>
    <button type="button" disabled={busy || !canCapture} className="device-config__button--primary"
      onClick={() => start()}>Capture Logs to Server</button>
    <button type="button" disabled={busy || !canCapture} onClick={() => start(true)}>Capture and download ZIP</button>
    {collecting && <button type="button" disabled={stopping} onClick={() => { stop.current = true; setStopping(true); }}>
      {stopping ? 'Stopping after this device…' : 'Stop after this device'}</button>}
    </div>
    <p>{selectedTargets.length} device(s) selected · {logTypes.length} log type(s) · previous {minutes} minute(s)</p>
    {progress && <div role="status" className="device-config__notice"><strong>{collecting ? 'Capturing logs…' : busy ? workLabel : 'Capture results'}</strong>
      <ul>{progress.map((item) => <li key={item.deviceId}>{item.name} · {item.status.replace(/_/g, ' ')}</li>)}</ul>
      {progress.some((item) => item.status === 'admission_uncertain') && <p>A capture response was not received. Check saved history before retrying; the same request key is retained.</p>}
    </div>}
    {serverResult && <div role="status" className={`device-config__notice${serverResult.complete ? '' : ' device-config__notice--warning'}`}>
      <p>{serverResult.complete ? 'Logs saved to server.' : 'Partial capture saved to server; some device logs are missing.'}</p>
      <p>Temporary folder: <code>{serverResult.directory}/</code> · Expires {new Date(serverResult.expiresAtEpochMs).toLocaleString()}.</p>
      <p>The previous log set for this site has been replaced. Check manifest.json in this folder for device results.</p>
    </div>}
    {packageResult && <p role="status" className={`device-config__notice${packageResult.complete ? '' : ' device-config__notice--warning'}`}>
      {packageResult.complete ? 'ZIP ready.' : 'Partial ZIP: some device logs are missing.'} Included {packageResult.devices.filter((item) => item.file).length}
      {' '}of {packageResult.devices.length} devices. See manifest.json in the ZIP for details.</p>}
    {packageResult?.devices.some((item) => item.suitestreamMatchingLines === 0) && <p role="status">
      Some devices had no matching Suitestream app messages in the selected capture and message levels. Their app log files are empty.</p>}
    {packageItems && <>
      <button type="button" disabled={busy} onClick={() => run(() => saveToServer(packageItems))}>Save captured logs to server</button>
      <button type="button" disabled={busy} onClick={() => run(() => downloadPackage(packageItems))}>Download this package</button>
    </>}
    <button type="button" disabled={busy || !selectedTargets.length || !levels.length || !logTypes.length || unsupported}
      onClick={downloadSaved}>Download latest saved ZIP</button>
    <p>The saved ZIP uses the latest capture matching the selected window for each device. It works with offline relays;
      capture dates and missing devices are listed in the package.</p>
    {error && <p role="alert">{error}</p>}
    <h4>Saved captures for selected devices</h4>
    {!selected.length && <p className="device-config__empty">No saved snapshots for the selected devices yet.</p>}
    <ul className="device-config__history">{selected.map((capture) => <li key={capture.id} data-status={capture.status}>
      <strong>{deviceName(capture.targetDeviceId, snapshot)}</strong>
      <p role="status">Previous {capture.minutes} minute{capture.minutes > 1 ? 's' : ''} · {capture.status} · {new Date(capture.createdAtEpochMs).toLocaleString()}</p>
      <p>Expires {new Date(capture.expiresAtEpochMs).toLocaleString()} · {capture.sizeBytes} bytes{capture.relayDeviceId ? ` · via ${deviceName(capture.relayDeviceId, snapshot)}` : ''}</p>
      {(capture.scanTruncated || capture.tailTruncated || capture.sourceLineLimitReached || capture.oversizedLinesDropped > 0) && <p>Capture reached a limit; some logs were omitted.</p>}
      {pending(capture) && <p>Reading existing logs and saving the snapshot. This finishes automatically.</p>}
      {capture.status === 'failed' && <p className="device-config__notice device-config__notice--error">{failureMessage(capture.error)}</p>}
      {capture.status === 'succeeded' && <>
        <button type="button" disabled={busy} onClick={() => view(capture)}>View logs</button>
        <button type="button" disabled={busy} onClick={() => download(capture)}>Download logs</button>
      </>}
      {pending(capture) ? <button type="button" disabled={busy} onClick={() => run(async () => { const result = await cancelDeviceLog(siteId, capture.id); if (alive.current) update(result.capture); })}>Cancel capture</button>
        : <button type="button" className="device-config__button--danger" disabled={busy} onClick={() => run(async () => {
          await deleteDeviceLog(siteId, capture.id);
          if (alive.current) { setCaptures((previous) => previous.filter((item) => item.id !== capture.id)); if (viewer?.id === capture.id) setViewer(null); }
        })}>Delete logs</button>}
    </li>)}</ul>
    {viewer && <div className="device-config__viewer" aria-label="Saved log viewer">
      <label>Search captured logs <input value={search} maxLength={200} onChange={(event) => setSearch(event.target.value)} /></label>
      <p>{matches.length} matching line(s). Showing up to 500 lines; download for the full snapshot.</p>
      <pre>{matches.slice(0, 500).join('\n')}</pre>
      <button type="button" onClick={() => { setViewer(null); readGeneration.current++; }}>Close logs</button>
    </div>}
  </section>;
}
